// The two pieces of the Threads read/refresh contract that must be right under
// concurrency, kept framework-free so their adversarial schedules are unit-testable at
// the caller boundary (#3139 U1–U5, re-audit findings 2 and 3).
//
// SnapshotSequencer — the client never MERGES thread events. Every event (a reply, a
// read on another device, its own read's answer, a reconnect) only asks for the
// canonical snapshot again. Requests are SERIALIZED: one in flight per context; a
// trigger during the flight sets a dirty flag and ONE more request follows when it
// settles. So two responses can never race — the later request always acquired its
// database snapshot after the earlier one finished — and a context change (workspace,
// connection) invalidates the flight's generation so its late answer applies nothing.
//
// ThreadReadController — a read is an OPERATION with a generation: its answer or
// failure settles only the pending entry AND the optimistic overlay it created (both
// compare-and-delete), the retried cursor is the greatest one explicitly viewed, and a
// retry never invents a fresh clock — an unviewed reply received while offline stays
// unread.

export class SnapshotSequencer<T> {
  private generation = 0;
  private busy = false;
  private dirty = false;
  // Callers that triggered during a flight wait for the follow-up that reflects them.
  private waiters: Array<() => void> = [];
  private timer: ReturnType<typeof setTimeout> | null = null;
  private fetches = 0;

  constructor(
    private readonly deps: {
      fetch(): Promise<T>;
      // `fetchNo`: this snapshot's request number (1-based, in issue order), so a
      // consumer can tell a snapshot requested BEFORE some moment from one after it.
      apply(snapshot: T, fetchNo: number): void;
      // A failed fetch is reported, never applied; the next trigger retries.
      onError?(err: unknown): void;
    },
  ) {}

  // Coalesce a burst of triggers (a reply fans out thread_updated to N followers, a
  // read answers AND broadcasts) into one request.
  schedule(delayMs = 150): void {
    if (this.timer) return;
    this.timer = setTimeout(() => {
      this.timer = null;
      void this.run();
    }, delayMs);
  }

  // Resolves when the snapshot that reflects THIS call has been applied (or failed): the
  // flight started now, or — during a flight — the ONE follow-up that runs after it.
  run(): Promise<void> {
    if (this.busy) {
      this.dirty = true;
      return new Promise((resolve) => this.waiters.push(resolve));
    }
    return this.flight();
  }

  private async flight(): Promise<void> {
    this.busy = true;
    const generation = this.generation;
    const fetchNo = ++this.fetches;
    try {
      const snapshot = await this.deps.fetch();
      // reset() ran meanwhile: this answer belongs to a context that is gone.
      if (generation === this.generation) this.deps.apply(snapshot, fetchNo);
    } catch (err) {
      if (generation === this.generation) this.deps.onError?.(err);
    } finally {
      this.busy = false;
    }
    if (!this.dirty) return;
    this.dirty = false;
    const waiters = this.waiters;
    this.waiters = [];
    // flight() never rejects (its fetch errors are reported to onError), so this runs
    // once the follow-up has applied or failed.
    void this.flight().finally(() => {
      for (const resolve of waiters) resolve();
    });
  }

  // A new context (workspace switched, socket replaced): whatever is in flight applies
  // nothing, no follow-up runs for it, and the next trigger starts clean.
  reset(): void {
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.generation += 1;
    this.dirty = false;
    for (const resolve of this.waiters) resolve();
    this.waiters = [];
  }

  // No snapshot in flight, queued or scheduled: the last applied one is the newest.
  get idle(): boolean {
    return !this.busy && !this.dirty && this.timer === null;
  }

  // How many fetches were issued so far — the number the NEXT one will exceed.
  get fetchCount(): number {
    return this.fetches;
  }
}

export interface PendingThreadRead {
  workspaceId: string;
  // Epoch ms the viewer explicitly saw up to. Captured at the click — never
  // re-stamped on retry.
  viewedAt: number;
  generation: number;
}

export class ThreadReadController<Answer = unknown> {
  private generation = 0;
  private readonly pending = new Map<string, PendingThreadRead>();

  constructor(
    private readonly deps: {
      send(workspaceId: string, rootId: string, viewedAt: number): Promise<Answer>;
      // Optimistic overlay on the local row, owned by this operation's generation.
      onLocalRead(rootId: string, generation: number): void;
      // The relay answered (success or refusal) THIS operation. The consumer lifts the
      // overlay only if this generation still owns it — a newer read of the same root
      // keeps its own — and only by a snapshot requested after the answer.
      onSettled(rootId: string, generation: number): void;
      refresh(): void;
      // The relay confirmed THIS read. Returns true when its answer already settles the
      // row and the totals, so no snapshot is needed (CYBORG-1192). `othersPending`:
      // another read is still unconfirmed, so the answer's totals do not include it.
      confirmed?(
        rootId: string,
        generation: number,
        answer: Answer,
        othersPending: boolean,
      ): boolean;
    },
  ) {}

  // The greatest cursor ever handed in for this root wins: an older tab's snapshot
  // after a newer one must not narrow the read.
  read(workspaceId: string, rootId: string, viewedAt: number): void {
    const prev = this.pending.get(rootId);
    const cursor = Math.max(viewedAt, prev?.workspaceId === workspaceId ? prev.viewedAt : 0);
    const generation = ++this.generation;
    this.pending.set(rootId, { workspaceId, viewedAt: cursor, generation });
    this.deps.onLocalRead(rootId, generation);
    void this.settle(rootId, generation, this.deps.send(workspaceId, rootId, cursor));
  }

  private async settle(rootId: string, generation: number, sent: Promise<Answer>): Promise<void> {
    let confirmed: { answer: Answer } | null = null;
    try {
      confirmed = { answer: await sent };
      // Compare-and-delete: a newer read of the same root owns the entry now.
      if (this.pending.get(rootId)?.generation === generation) this.pending.delete(rootId);
    } catch {
      // Refused or offline: kept pending for the next flush (workspace open / reconnect).
    }
    this.deps.onSettled(rootId, generation);
    const othersPending = this.pending.size > 0;
    if (confirmed && this.deps.confirmed?.(rootId, generation, confirmed.answer, othersPending)) {
      return;
    }
    this.deps.refresh();
  }

  // Re-send every unconfirmed read of this workspace with its CAPTURED cursor.
  flush(workspaceId: string): void {
    for (const [rootId, p] of Array.from(this.pending.entries())) {
      if (p.workspaceId !== workspaceId) continue;
      this.pending.delete(rootId);
      this.read(workspaceId, rootId, p.viewedAt);
    }
  }

  pendingFor(rootId: string): PendingThreadRead | undefined {
    return this.pending.get(rootId);
  }

  get size(): number {
    return this.pending.size;
  }
}

// A relay older than a message answers `respondError("unsupported: <type>")`.
export function isUnsupportedByRelay(err: unknown): boolean {
  return err instanceof Error && err.message.startsWith("unsupported:");
}
